import React, { useEffect, useState } from 'react';
import { Container } from '../ui/Container';
import { Button } from '../ui/Button';
import { getImagePath } from '../../Utils/Utilities';
import apiService from '../../Utils/APIService';
import { useIsMobile } from '../../Hooks/useIsMobile';
import { toast } from 'sonner';
import { AlertBox } from '../Common/AlertBox';
import { useBrand } from '../../Utils/BrandContext';


const ContactSection = ({ isFromLP = false, className = '' }) => {
    const [form, setForm] = useState({ name: '', company: '', mobile: '', notes: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showThankYou, setShowThankYou] = useState(false);
    const [showMobileForm, setShowMobileForm] = useState(false);
    const [showCallbackDialog, setShowCallbackDialog] = useState(false);
    const [callbackNotes, setCallbackNotes] = useState('');
    const [callbackBusiness, setCallbackBusiness] = useState('');
    const [callbackSoftware, setCallbackSoftware] = useState('');
    const [callbackSubmitted, setCallbackSubmitted] = useState(false);
    const isMobile = useIsMobile();
    const { brand: activeBrand } = useBrand()

    const getLeadInfo = () => {
        try {
            const allDataRaw = localStorage.getItem('allData');
            if (!allDataRaw) return { isFirstTime: false, leadPOCID: 0, mobile: '', name: '', companyName: '', email: '', source: '' };
            const allData = JSON.parse(allDataRaw);
            const leadInfo = allData.info ? JSON.parse(allData.info) : [];
            let isFirstTime = false;
            let leadPOCID = 0;
            const mobile = allData.mobile || '';
            const name = `${allData.firstName || ''} ${allData.lastName || ''}`.trim();
            const companyName = allData.companyName || '';
            const email = allData.email || '';
            const source = allData.source || '';
            if (leadInfo.length > 0) {
                if (leadInfo[0].LogCnt == 0) {
                    isFirstTime = true;
                }
                if (String(leadInfo[0].LeadPOCID) != "undefined" && !isNaN(leadInfo[0].LeadPOCID)) {
                    leadPOCID = leadInfo[0].LeadPOCID;
                }
            }
            return { isFirstTime, leadPOCID, mobile, name, companyName, email, source };
        } catch {
            return { isFirstTime: false, leadPOCID: 0, mobile: '', name: '', companyName: '', email: '', source: '' };
        }
    };

    const { isFirstTime, leadPOCID, mobile: leadMobile, name: leadName, companyName: leadCompany, email: leadEmail, source: leadSource } = getLeadInfo();
    const showFloatingBtn = isFromLP && isMobile && !isFirstTime && leadPOCID != 0;

    const [showDemoAlert, setShowDemoAlert] = useState(false);

    const handleCallbackSubmit = async () => {
        setIsSubmitting(true);
        try {
            await apiService.sendCallbackReqGuide({
                mobile: leadMobile,
                question: callbackNotes,
                companyName: leadCompany,
                name: leadName,
                eml: leadEmail ? `${leadEmail}` : ``,
                business: callbackBusiness,
                useSoftware: callbackSoftware
            });
            setShowCallbackDialog(false);
            setShowDemoAlert(true);
        } catch {
            setShowCallbackDialog(false);
            setShowDemoAlert(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDesktopSubmit = async () => {
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            const firstError = Object.values(validationErrors)[0];
            toast.error(firstError);
            return;
        }
        setErrors({});
        setIsSubmitting(true);
        try {
            if (isFromLP) {
                await apiService.sendCallbackReqGuide({
                    mobile: leadMobile || form.mobile,
                    question: form.notes,
                    companyName: leadCompany || form.company,
                    name: leadName || form.name,
                    eml: leadEmail ? `${leadEmail}` : ``,
                    business: callbackBusiness,
                    useSoftware: callbackSoftware
                });
            } else {
                await apiService.sendCallbackRequest(form);
            }
            setShowDemoAlert(true);
        } catch {
            setShowDemoAlert(true);
        } finally {
            setIsSubmitting(false);
        }
    };
    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const [errors, setErrors] = useState({});
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    useEffect(() => {
        if (!isFromLP) return;
        const interval = setInterval(() => {
            setCurrentImageIndex((prev) => (prev + 1) % 4);
        }, 3000);
        return () => clearInterval(interval);
    }, [isFromLP]);

    const validate = () => {
        const newErrors = {};
        if (isFromLP) {
            if (!leadName?.trim() && !form.name.trim()) newErrors.name = 'Name is required';
            if (!leadCompany?.trim() && !form.company.trim()) newErrors.company = 'Company is required';
            if (!leadMobile?.trim() && !form.mobile.trim()) newErrors.mobile = 'Mobile is required';
            if (!callbackBusiness.trim()) newErrors.callbackBusiness = 'Please select your business type';
            if (!callbackSoftware.trim()) newErrors.callbackSoftware = 'Please enter software you currently use';
            if (!form.notes.trim()) newErrors.notes = 'Please enter your question';
        } else {
            if (!form.name.trim()) newErrors.name = 'Name is required';
            if (!form.company.trim()) newErrors.company = 'Company is required';
            if (!form.mobile.trim()) newErrors.mobile = 'Mobile is required';
            else if (form.mobile.length !== 10) newErrors.mobile = 'Enter valid 10-digit mobile number';
        }
        return newErrors;
    };
    const handleSubmit = async () => {
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            const firstError = Object.values(validationErrors)[0];
            toast.error(firstError);
            return;
        }
        setErrors({});
        setIsSubmitting(true);
        try {
            await apiService.sendCallbackRequest(form);
            setShowDemoAlert(true);
        } catch (e) {
            setShowDemoAlert(true);
        } finally {
            setIsSubmitting(false);
        }
    };
    if (isFromLP && isMobile) return (
        <>
            <AlertBox
                open={showDemoAlert}
                onOpenChange={setShowDemoAlert}
                title={activeBrand === 'qik' || activeBrand === 'qikerp' ? 'QikERP' : 'Biziverse'}
                description={<>Thank you! Our team will contact you shortly.<br /><br />Would you like to watch the 5-min demo video in the meantime?</>}
                actions={[
                    { label: 'Yes', value: 'no', variant: 'cancel' },
                    { label: 'Yes', value: 'yes', variant: 'primary' },
                ]}
                onAction={(val) => {
                    if (val === 'yes') {
                        const el = document.getElementById('demoSection');
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                    }
                    setShowDemoAlert(false);
                }}
            />
            <AlertBox
                open={showDemoAlert}
                onOpenChange={setShowDemoAlert}
                title={activeBrand === 'qik' || activeBrand === 'qikerp' ? 'QikERP' : 'Biziverse'}
                description={<>Thank you! Our team will contact you shortly.<br /><br />Would you like to watch the 5-min demo video in the meantime?</>}
                actions={[
                    { label: 'No', value: 'no', variant: 'cancel' },
                    { label: 'Yes', value: 'yes', variant: 'primary' },
                ]}
                onAction={(val) => {
                    if (val === 'yes') {
                        const el = document.getElementById('demoSection');
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                    }
                    setShowDemoAlert(false);
                }}
            />

            {showFloatingBtn && (
                <button
                    onClick={() => { setShowCallbackDialog(true); setCallbackSubmitted(false); setCallbackNotes(''); }}
                    className="fixed bottom-[6rem] right-4 z-50 bg-darkBlue hover:bg-darkBlue-dark text-white rounded-full shadow-2xl flex items-center justify-center"
                    style={{ width: 52, height: 52 }}
                    aria-label="Get a Callback"
                >
                    <i className="fas fa-phone text-xl"></i>
                </button>
            )}

            {showCallbackDialog && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
                    <div className="bg-white rounded w-full max-w-sm shadow-xl">
                        {/* Dialog Header */}
                        <div className="flex items-start justify-between px-4 pt-5">
                            <div>
                                <h3 className="text-2xl font-semibold text-text-primary">Get a Callback</h3>
                                <p className="text-[15px] text-text-secondary mt-0.5">from {activeBrand === 'qik' || activeBrand === 'qikerp' ? 'QikERP' : 'Biziverse'} Team</p>
                            </div>
                            <button
                                onClick={() => setShowCallbackDialog(false)}
                                className="text-text-secondary hover:text-text-primary text-xl leading-none mt-1"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Dialog Body */}
                        <div className="px-4 py-5">
                            {callbackSubmitted ? (
                                <div className="text-center py-8">
                                    <i className="fas fa-check-circle text-5xl text-primary mb-4 block"></i>
                                    <h4 className="text-lg font-bold text-primary mb-2">Thank You!</h4>
                                    <p className="text-text-secondary text-sm">Our team will call you back shortly.</p>
                                </div>
                            ) : (
                                <>
                                    <p className="text-sm text-text-primary mb-3">How may we help you?</p>
                                    <textarea
                                        value={callbackNotes}
                                        onChange={(e) => setCallbackNotes(e.target.value)}
                                        placeholder="Please enter your question here."
                                        rows={4}
                                        maxLength={200}
                                        className="w-full px-3 py-2 border-2 border-border rounded-[5px] bg-background-light text-text-primary text-[14px] focus:outline-none focus:border-primary hover:border-primary transition-all placeholder:text-gray-400 resize-none"
                                    />
                                    <p className="text-sm text-text-secondary mt-2 mb-4">
                                        We will call you on <span className="font-semibold text-text-primary">{leadMobile}</span>.
                                    </p>
                                    <Button
                                        onClick={handleCallbackSubmit}
                                        disabled={isSubmitting}
                                        size="sm"
                                    >
                                        <i className="fas fa-check mr-2"></i>
                                        Get a Callback
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );

    // Mobile Layout
    if (isMobile) {
        return (
            <>
                <AlertBox
                    open={showDemoAlert}
                    onOpenChange={setShowDemoAlert}
                    title={activeBrand === 'qik' || activeBrand === 'qikerp' ? 'QikERP' : 'Biziverse'}
                    description={<><span className="block mb-3">Thank you! Our team will contact you shortly.</span><span className="block">Would you like to watch the 5-min demo video in the meantime?</span></>}
                    actions={[
                        { label: 'No', value: 'no', variant: 'cancel' },
                        { label: 'Yes', value: 'yes', variant: 'primary' },
                    ]}
                    onAction={(val) => {
                        if (val === 'yes') {
                            const el = document.getElementById('demoSection');
                            if (el) el.scrollIntoView({ behavior: 'smooth' });
                        }
                        setShowDemoAlert(false);
                    }}
                />
                <section id="letsConnect" className="bg-background-light py-12">
                    <div className="px-6">
                        <div className="text-center mb-8">
                            <h2 className="text-[30px] md:text-[40px] font-bold secttl mb-3">
                                Let's Connect!
                            </h2>
                            <p className="text-[14px] md:text-lg text-text-primary">
                                Please share your contact details and our team will call you back!
                            </p>
                        </div>

                        {showThankYou ? (
                            <div className="text-center py-12">
                                <i className="fas fa-check-circle text-6xl text-primary mb-6 block"></i>
                                <h3 className="text-2xl font-bold text-primary mb-3">Thank You!</h3>
                                <p className="text-text-secondary text-base">Our team will call you back shortly.</p>
                            </div>
                        ) : showMobileForm ? (
                            <div className="space-y-4">
                                <div className="flex items-center gap-3">
                                    <i className="fas fa-user text-primary text-base w-5 text-center flex-shrink-0"></i>
                                    <div className="w-full">
                                        <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="Name" maxLength={100}
                                            className={`w-full h-11 px-3 py-2 border-2 rounded-[5px] bg-white text-text-primary text-[14px] focus:outline-none transition-all placeholder:text-gray-400 ${errors.name ? 'border-primary' : 'border-border focus:border-primary hover:border-primary'}`} />
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <i className="fa fa-industry text-primary text-base w-5 text-center flex-shrink-0"></i>
                                    <div className="w-full">
                                        <input type="text" name="company" value={form.company} onChange={handleChange} placeholder="Company" maxLength={100}
                                            className={`w-full h-11 px-3 py-2 border-2 rounded-[5px] bg-white text-text-primary text-[14px] focus:outline-none transition-all placeholder:text-gray-400 ${errors.company ? 'border-primary' : 'border-border focus:border-primary hover:border-primary'}`} />
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <i className="fas fa-phone text-primary text-base w-5 text-center flex-shrink-0"></i>
                                    <div className="w-full">
                                        <input type="text" name="mobile" value={form.mobile} onChange={handleChange} placeholder="Mobile No." maxLength={10}
                                            onKeyPress={(e) => !/[0-9]/.test(e.key) && e.preventDefault()}
                                            className={`w-full h-11 px-3 py-2 border-2 rounded-[5px] bg-white text-text-primary text-[14px] focus:outline-none transition-all placeholder:text-gray-400 ${errors.mobile ? 'border-primary' : 'border-border focus:border-primary hover:border-primary'}`} />
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <i className="fas fa-comment-alt text-primary text-base w-5 text-center flex-shrink-0 mt-3"></i>
                                    <textarea name="notes" value={form.notes} onChange={handleChange}
                                        placeholder={`How can we help you?\ne.g. "How do I join ${activeBrand === 'qik' || activeBrand === 'qikerp' ? 'QikERP' : 'Biziverse'}?"`}
                                        rows={4} maxLength={200}
                                        className="w-full px-3 py-2 border-2 border-border rounded-[5px] bg-white text-text-primary text-[14px] focus:outline-none focus:border-primary hover:border-primary transition-all placeholder:text-gray-400 resize-none" />
                                </div>
                                <div className="flex flex-col gap-3 pt-2 px-8">
                                    <Button onClick={handleSubmit} disabled={!form.name.trim() || !form.mobile.trim() || isSubmitting} className="w-full">
                                        Get a Callback
                                    </Button>
                                    <Button variant="outline" className="w-full"
                                        onClick={() => { const el = document.getElementById('demoSection'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}>
                                        Watch Demo
                                    </Button>
                                    <Button variant="outline" className="w-full"
                                        onClick={() => { const el = document.getElementById('hero-section'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}>
                                        Get Started
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-3 px-8">
                                <Button onClick={() => setShowMobileForm(true)} className="w-full">
                                    Get a Callback
                                </Button>
                                <Button variant="outline" className="w-full"
                                    onClick={() => { const el = document.getElementById('demoSection'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}>
                                    Watch Demo
                                </Button>
                                <Button variant="outline" className="w-full"
                                    onClick={() => { const el = document.getElementById('hero-section'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}>
                                    Get Started
                                </Button>
                            </div>
                        )}
                    </div>
                </section>
            </>
        );
    }

    return (
        <>
            <AlertBox
                open={showDemoAlert}
                onOpenChange={setShowDemoAlert}
                title={activeBrand === 'qik' || activeBrand === 'qikerp' ? 'QikERP' : 'Biziverse'}
                description={<><span className="block mb-3">Thank you! Our team will contact you shortly.</span><span className="block">Would you like to watch the 5-min demo video in the meantime?</span></>}
                actions={[
                    { label: 'No', value: 'no', variant: 'cancel' },
                    { label: 'Yes', value: 'yes', variant: 'primary' },
                ]}
                onAction={(val) => {
                    if (val === 'yes') {
                        const el = document.getElementById('demoSection');
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                    }
                    setShowDemoAlert(false);
                }}
            />
            <section id="letsConnect" className="bg-white py-12 md:py-16 lg:py-20 md:px-40">
                <Container variant="wide">
                    <div className="text-center mb-10 md:mb-14">
                        <h2 className="m-0 text-[30px] md:text-[40px] font-bold secttl text-text-primary mb-3">
                            {isFromLP ? 'Contact Us' : "Let's Connect!"}
                        </h2>
                        {!isFromLP && <p className="text-[14px] md:text-lg text-text-primary">
                            Please share your contact details and our team will call you back!
                        </p>}
                    </div>

                    <div className="flex flex-col md:flex-row items-center justify-center gap-10 md:gap-40">
                        <div className="w-full md:w-[60%]">
                            {isFromLP ? (
                                <div className="relative w-full h-auto flex justify-center">
                                    {[
                                        getImagePath('/Images/BiziPeach1.png'),
                                        getImagePath('/Images/BiziModi1.png'),
                                        getImagePath('/Images/BiziPeacock1.png'),
                                        getImagePath('/Images/BiziTeam1.png')
                                    ].map((image, index) => (
                                        <img
                                            key={index}
                                            src={image}
                                            alt={`Team ${index + 1}`}
                                            className={`w-[84%] h-auto rounded-[5px] shadow-medium object-cover transition-opacity duration-500 ${index === currentImageIndex ? 'opacity-100' : 'opacity-0 absolute top-0 left-0'}`}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <img src={`${getImagePath("/Images/webTeam.jpg")}`} alt="Our Team"
                                    className="w-full rounded-[5px] shadow-medium object-cover" />
                            )}
                        </div>

                        <div className="w-full md:w-[35%]">
                            {showThankYou ? (
                                <div className="text-center py-12">
                                    <i className="fas fa-check-circle text-6xl text-primary mb-6 block"></i>
                                    <h3 className="text-2xl font-bold text-primary mb-3">Thank You!</h3>
                                    <p className="text-text-secondary text-base">Our team will call you back shortly.</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {!isFromLP && (
                                        <>
                                            <div className="flex items-center gap-3">
                                                <i className="fas fa-user text-primary text-base w-5 text-center flex-shrink-0"></i>
                                                <div className="w-full">
                                                    <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="Name" maxLength={100}
                                                        className={`w-full h-11 px-3 py-2 border-2 rounded-[5px] bg-background-light text-text-primary text-[14px] focus:outline-none transition-all placeholder:text-gray-400 ${errors.name ? 'border-primary' : 'border-border focus:border-primary hover:border-primary'}`} />
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <i className="fa fa-industry text-primary text-base w-5 text-center flex-shrink-0"></i>
                                                <div className="w-full">
                                                    <input type="text" name="company" value={form.company} onChange={handleChange} placeholder="Company" maxLength={100}
                                                        className={`w-full h-11 px-3 py-2 border-2 rounded-[5px] bg-background-light text-text-primary text-[14px] focus:outline-none transition-all placeholder:text-gray-400 ${errors.company ? 'border-primary' : 'border-border focus:border-primary hover:border-primary'}`} />
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <i className="fas fa-phone text-primary text-base w-5 text-center flex-shrink-0"></i>
                                                <div className="w-full">
                                                    <input type="text" name="mobile" value={form.mobile} onChange={handleChange} placeholder="Mobile No." maxLength={10}
                                                        onKeyPress={(e) => !/[0-9]/.test(e.key) && e.preventDefault()}
                                                        className={`w-full h-11 px-3 py-2 border-2 rounded-[5px] bg-background-light text-text-primary text-[14px] focus:outline-none transition-all placeholder:text-gray-400 ${errors.mobile ? 'border-primary' : 'border-border focus:border-primary hover:border-primary'}`} />
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    {isFromLP && (
                                        <>
                                            <div>
                                                <p className="text-sm md:text-[16px] text-text-primary mb-2 font-semibold">What kind of business do you run?</p>
                                                <select
                                                    value={callbackBusiness}
                                                    onChange={(e) => setCallbackBusiness(e.target.value)}
                                                    className="w-full h-11 px-3 py-2 border border-border rounded-[5px] bg-background-light text-text-primary text-[14px] focus:outline-none focus:border-primary hover:border-primary transition-all"
                                                >
                                                    <option value="">Select Business</option>
                                                    <option value="Manufacturing">Manufacturing</option>
                                                    <option value="Trading">Trading</option>
                                                    <option value="Services">Services</option>
                                                    <option value="Retail">Retail</option>
                                                    <option value="Consultancy">Consultancy</option>
                                                </select>
                                            </div>
                                            <div>
                                                <p className="text-sm md:text-[16px] text-text-primary mb-2 font-semibold">What software do you currently use?</p>
                                                <input
                                                    type="text"
                                                    value={callbackSoftware}
                                                    onChange={(e) => setCallbackSoftware(e.target.value)}
                                                    placeholder="Please enter software you are using currently."
                                                    maxLength={100}
                                                    className="w-full h-11 px-3 py-2 border border-border rounded-[5px] bg-background-light text-text-primary text-[14px] focus:outline-none focus:border-primary hover:border-primary transition-all placeholder:text-gray-400"
                                                />
                                            </div>
                                        </>
                                    )}
                                    {!isFromLP ? (
                                        <div className="flex items-start gap-3">
                                            <i className="fas fa-comment-alt text-primary text-base w-5 text-center flex-shrink-0 mt-3"></i>
                                            <div className="w-full">
                                                <textarea name="notes" value={form.notes} onChange={handleChange}
                                                    placeholder={`How can we help you?\ne.g. "How do I join ${activeBrand === 'qik' || activeBrand === 'qikerp' ? 'QikERP' : 'Biziverse'}?"`}
                                                    rows={4} maxLength={200}
                                                    className="w-full px-3 py-2 border-2 border-border rounded-[5px] bg-background-light text-text-primary text-[14px] focus:outline-none focus:border-primary hover:border-primary transition-all placeholder:text-gray-400 resize-none" />
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <p className="text-sm md:text-[16px] text-text-primary mb-2 font-semibold">How may we help you?</p>
                                            <textarea name="notes" value={form.notes} onChange={handleChange}
                                                placeholder="Please enter the your question here."
                                                rows={4} maxLength={200}
                                                className="w-full px-3 py-2 border border-border rounded-[5px] bg-background-light text-text-primary text-[14px] focus:outline-none focus:border-primary hover:border-primary transition-all placeholder:text-gray-400 resize-none" />
                                        </div>
                                    )}

                                    {!isFromLP && <div className="flex flex-col gap-3 pt-2 ml-8">
                                        {isFromLP && leadMobile && (
                                            <p className="text-sm text-text-secondary mb-1">
                                                We will call you on <span className="font-semibold text-text-primary">{leadMobile}</span>.
                                            </p>
                                        )}
                                        <Button onClick={handleDesktopSubmit} disabled={isFromLP ? isSubmitting : (!form.name.trim() || !form.mobile.trim() || isSubmitting)} size="sm" className="w-full">
                                            Get a Callback
                                        </Button>
                                        {!isFromLP && (
                                            <div className="flex items-center gap-3">
                                                <Button variant="outline" size="sm" className="flex-1"
                                                    onClick={() => { const el = document.getElementById('demoSection'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}>
                                                    Watch Demo
                                                </Button>
                                                <Button variant="outline" size="sm" className="flex-1"
                                                    onClick={() => { const el = document.getElementById('hero-section'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}>
                                                    Get Started
                                                </Button>
                                            </div>
                                        )}
                                    </div>}

                                </div>
                            )}
                            {isFromLP && <div className=" ">
                                {isFromLP && leadMobile && (
                                    <p className="text-sm text-text-secondary mb-1 mt-2">
                                        We will call you on <span className="font-semibold text-text-primary">{leadMobile}</span>.
                                    </p>
                                )}
                                <Button onClick={handleDesktopSubmit} disabled={isFromLP ? isSubmitting : (!form.name.trim() || !form.mobile.trim() || isSubmitting)} size="sm" className="mt-4" >
                                    Get a Callback
                                </Button>
                            </div>}
                        </div>
                    </div>
                </Container>
            </section>
        </>
    );
};

export default ContactSection;